'use client';
/** @jsxImportSource @emotion/react */

import {
  useFloating,
  autoUpdate,
  offset,
  shift,
  useRole,
  useHover,
  useInteractions,
  arrow,
} from '@floating-ui/react';
import React, {
  type ElementType,
  forwardRef,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type OverridableComponent,
  type OverrideProps,
} from '@mui/material/OverridableComponent';
import MuiSlider, {
  type SliderTypeMap as MuiSliderTypeMap,
} from '@mui/material/Slider';

import { Box } from '../box/Box';
import { Stack } from '../stack/Stack';
import { Typography, type TypographyProps } from '../typography/Typography';
import { Histogram, type HistogramProps } from './components/Histogram';
import { useDetectOverlap } from './hooks/useDetectOverlap';
import { useForceUpdate } from './hooks/useForceUpdate';
import { isTuple } from './utils/isTuple';

type ExcludedMuiProps = 'orientation';

interface CustomProps<T> {
  component?: ElementType;
  /**
   * @type number[]
   * - Each value is a decimal between 0 and 1.
   * - Number of values will determine number of bars shown.
   */
  histogram?: HistogramProps['bars'];
  valueLabelJoin?: T extends number[] ? boolean | string : never;
}

type SliderTypeMap<
  T = number[] | number,
  D extends ElementType = MuiSliderTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: CustomProps<T> & Omit<MuiSliderTypeMap['props'], ExcludedMuiProps>;
};

export type SliderProps<
  T = number[] | number,
  D extends ElementType = MuiSliderTypeMap['defaultComponent'],
> = OverrideProps<SliderTypeMap<T, D>, D>;

const SliderComponent = <T extends number[] | number>(
  {
    histogram,
    valueLabelDisplay,
    valueLabelFormat,
    valueLabelJoin,
    ...props
  }: SliderProps<T>,
  ref: Ref<HTMLSpanElement>
) => {
  const trackRef = useRef<HTMLSpanElement | null>(null);
  const labelRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const thumbRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [referenceProps, setReferenceProps] = useState<
    React.HTMLProps<HTMLSpanElement>[]
  >([]);

  const { size, value } = props;

  if (value === undefined) {
    throw new Error('Value cannot be undefined');
  }

  const setLabelRefs = (element: HTMLSpanElement, index: number) => {
    if (!labelRefs.current) {
      return;
    }
    labelRefs.current[index] = element;
  };

  const updateReferenceProps = (
    newProps: React.HTMLProps<HTMLSpanElement>,
    index: number
  ) => {
    setReferenceProps((prev) => {
      const updatedProps = [...prev];
      updatedProps[index] = newProps;
      return updatedProps;
    });
  };

  const forceRerender = useForceUpdate();

  const isTupleValue = isTuple(value);
  const isOverlapping = useDetectOverlap(
    labelRefs?.current?.[0] ?? null,
    labelRefs?.current?.[1] ?? null
  );

  const labelDisplayValues = useMemo(() => {
    // If a formatter function is given, transform each value using this function
    if (typeof valueLabelFormat === 'function') {
      return Array.isArray(value)
        ? value.map(valueLabelFormat)
        : [valueLabelFormat(value, 0)];
    }
    // Otherwise just return the unmodified value(s) as an array
    return Array.isArray(value) ? value : [value];
  }, [value, valueLabelFormat]);

  const shouldCombine = useMemo(() => {
    return !!(valueLabelJoin && isTupleValue && isOverlapping);
  }, [valueLabelJoin, isTupleValue, isOverlapping]);

  const combinedLabelDisplayValue = useMemo(() => {
    if (shouldCombine) {
      const joiner = typeof valueLabelJoin === 'string' ? valueLabelJoin : '-';
      const [from, to] = labelDisplayValues;
      return [from, ` ${joiner} `, to];
    } else {
      return null;
    }
  }, [shouldCombine, valueLabelJoin, labelDisplayValues]);

  const transformHandler = useCallback(
    (val: number, index: number) => {
      const displayValue = labelDisplayValues[index];
      return displayValue;
    },
    [labelDisplayValues]
  );

  useEffect(() => {
    // This is a hack to ensure the combined label calculates correctly on first page load.
    if (referenceProps) {
      forceRerender();
    }
  }, [forceRerender, referenceProps]);

  const thumbRefHandler = (element: HTMLSpanElement) => {
    if (!element) {
      return;
    }
    const index = Number(element?.getAttribute('data-index')) ?? 0;
    return (thumbRefs.current[index] = element);
  };

  return (
    <>
      {histogram ? <Histogram bars={histogram} /> : null}
      <MuiSlider
        ref={ref}
        slotProps={{
          root: {
            ref: trackRef,
          },
          thumb: () => ({
            children: { ...referenceProps },
            ref: thumbRefHandler,
          }),
          track: {
            children: shouldCombine && (
              <CombinedLabel size={size} trackRef={trackRef}>
                {combinedLabelDisplayValue}
              </CombinedLabel>
            ),
          },
          valueLabel: () => ({
            labelRefs,
            setLabelRefs,
            setReferenceProps: (
              newProps: React.HTMLProps<HTMLSpanElement>,
              index: number
            ) => updateReferenceProps(newProps, index),
            shouldCombine,
            thumbRefs,
            trackRef,
            valueLabelDisplay,
            valueLabelJoin,
          }),
        }}
        slots={{
          valueLabel: SingleLabel,
        }}
        valueLabelDisplay={valueLabelDisplay}
        valueLabelFormat={transformHandler}
        {...props}
      />
    </>
  );
};

const SliderLabelText = React.forwardRef<
  HTMLSpanElement,
  TypographyProps & { isPositioned: boolean }
>(({ isPositioned, sx = [], ...props }, ref) => {
  return (
    <Typography
      component="span"
      ref={ref}
      sx={[
        (theme) => ({
          bgcolor: theme.palette.grey[600],
          borderRadius: `${theme.shape.borderRadius}px`,
          color: 'white',
          display: isPositioned ? 'inline' : 'none',
          px: theme.spacing(1),
          py: theme.spacing(0.5),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      align="inherit"
      data-testid="sliderLabel"
      variant="subtitle2"
      {...props}
    />
  );
});

type SingleLabelProps = React.PropsWithChildren<Partial<TypographyProps>> & {
  setLabelRefs: (
    element: React.MutableRefObject<HTMLSpanElement | null>,
    index: number
  ) => void;
  setReferenceProps: (
    props: React.HTMLProps<HTMLSpanElement>,
    index: number
  ) => void;
  index: number;
  shouldCombine: boolean;
  thumbRefs: React.MutableRefObject<(HTMLSpanElement | null)[]>;
  trackRef: React.MutableRefObject<HTMLSpanElement | null>;
  value: number;
  valueLabelDisplay: 'auto' | 'off' | 'on';
};
export const SingleLabel: React.FC<SingleLabelProps> = ({
  children,
  index,
  setLabelRefs,
  setReferenceProps,
  shouldCombine,
  thumbRefs,
  trackRef,
  value,
  valueLabelDisplay,
}) => {
  const arrowRef = useRef<HTMLSpanElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(
    valueLabelDisplay === 'on' ? true : false
  );

  const updateOpen = (open: boolean) => {
    if (valueLabelDisplay === 'auto') {
      setIsOpen(open);
    }
  };

  const { context, floatingStyles, isPositioned, middlewareData, refs } =
    useFloating({
      middleware: [
        shift(() => {
          return {
            boundary: {
              height: 0,
              width: trackRef?.current?.getBoundingClientRect().width ?? 0,
              x: trackRef?.current?.getBoundingClientRect().left ?? 0,
              y: 0,
            },
            // MUI slider thumb extends 10px beyond the track boundary
            padding: { left: -10, right: -10 },
          };
        }),
        arrow({
          element: arrowRef,
        }),
        offset(8),
      ],
      onOpenChange: updateOpen,
      open: isOpen,
      placement: 'top',
      // Make sure the tooltip stays on the screen
      whileElementsMounted: autoUpdate,
    });

  // Role props for screen readers
  const role = useRole(context, { role: 'tooltip' });

  // Use hover to set to open when valueLabelDisplay is 'auto'
  const hover = useHover(context);

  // Merge all the interactions into prop getters
  const { getFloatingProps, getReferenceProps } = useInteractions([
    role,
    hover,
  ]);

  useEffect(() => {
    refs.setReference(thumbRefs?.current?.[index]);
  }, [thumbRefs, refs, index]);

  useEffect(() => {
    setReferenceProps && setReferenceProps(getReferenceProps(), index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // The empty dependency array ensures this effect runs only once.

  useEffect(() => {
    refs?.floating?.current &&
      setLabelRefs(
        refs.floating
          .current as unknown as React.MutableRefObject<HTMLSpanElement | null>,
        index
      );
  }, [setLabelRefs, refs, index]);

  return (
    <>
      {isOpen && (
        <Stack alignItems="center">
          <SliderLabelText
            ref={refs.setFloating}
            aria-hidden={shouldCombine}
            isPositioned={isPositioned}
            style={floatingStyles}
            sx={{ visibility: shouldCombine ? 'hidden' : 'visible' }}
            {...getFloatingProps()}
          >
            {value}
            <Box
              ref={arrowRef}
              sx={(theme) => ({
                bgcolor: theme.palette.grey[600],
                height: theme.spacing(1),
                left: middlewareData.arrow?.x,
                position: 'absolute',
                top: middlewareData.arrow?.y,
                transform: 'rotate(45deg)',
                visibility: isPositioned ? 'visible' : 'hidden',
                width: theme.spacing(1),
              })}
            />
          </SliderLabelText>
        </Stack>
      )}
      {children}
    </>
  );
};

type CombinedLabelProps = React.PropsWithChildren<{
  size: 'medium' | 'small' | undefined; // get size from slider props
  trackRef: React.MutableRefObject<HTMLSpanElement | null>;
}>;
const CombinedLabel: React.FC<CombinedLabelProps> = ({
  children,
  size,
  trackRef,
  ...props
}) => {
  const { context, floatingStyles, isPositioned, refs } = useFloating({
    middleware: [
      shift(() => {
        return {
          boundary: {
            height: 0,
            width: trackRef?.current?.getBoundingClientRect().width ?? 0,
            x: trackRef?.current?.getBoundingClientRect().left ?? 0,
            y: 0,
          },
          // MUI slider thumb extends 10px beyond the track boundary
          padding: { left: -10, right: -10 },
        };
      }),
      // "magic" numbers that work with MUI slider spacing and single label offset of 8px to avoid label jump
      offset(size === 'small' ? 13 : 16),
    ],
    placement: 'top',
    strategy: 'absolute',
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
  });

  // Role props for screen readers
  const role = useRole(context, { role: 'tooltip' });

  // Merge all the interactions into prop getters
  const { getFloatingProps, getReferenceProps } = useInteractions([role]);
  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps} />
      <Stack alignItems="center">
        <SliderLabelText
          ref={refs.setFloating}
          isPositioned={isPositioned}
          style={{
            ...floatingStyles,
            width: 'max-content',
          }}
          {...getFloatingProps()}
          {...props}
        >
          {children}
        </SliderLabelText>
      </Stack>
    </>
  );
};

SliderComponent.displayName = 'Slider';

export const Slider = forwardRef(
  SliderComponent
) as OverridableComponent<SliderTypeMap>;
